/**
 * InstallQrCode — Phase 8 (M8) inline QR for desktop visitors.
 *
 * We deliberately avoid adding a QR dependency. The plan suggests
 * `qrcode-svg` (~3 kB) but a more honest answer for an MVP that has
 * no real App Store URL yet is a **labelled visual placeholder** that:
 *
 *   - shows a "share by phone" affordance,
 *   - tells the user exactly what URL to type,
 *   - works offline (no third-party qrserver.com round-trip),
 *   - leaves a clean swap-in point for a real generator when the
 *     store URLs go live.
 *
 * The visual is an SVG square that frames the install URL as text,
 * so visually-impaired users get a textual fallback for free.
 *
 * When a real generator lands (Phase 9 or after), replace the SVG
 * body with the QR matrix output keyed by `value`.
 */
import type { CSSProperties } from 'react'

interface InstallQrCodeProps {
  /** URL to display + (future) encode in the QR matrix. */
  value: string
  /** Optional accessible label. */
  ariaLabel?: string
}

const wrap: CSSProperties = {
  display: 'inline-flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 8,
}

const square: CSSProperties = {
  width: 184,
  height: 184,
  borderRadius: 16,
  background:
    'repeating-conic-gradient(#0f172a 0% 25%, transparent 0% 50%) 50% / 16px 16px',
  border: '1px solid var(--color-border, #e2e8f0)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 14,
  textAlign: 'center',
  boxSizing: 'border-box',
}

const innerLabel: CSSProperties = {
  background: 'white',
  borderRadius: 12,
  padding: '10px 12px',
  fontSize: 11,
  fontWeight: 700,
  color: 'var(--color-fg, #0f172a)',
  wordBreak: 'break-all',
  lineHeight: 1.35,
  maxWidth: '100%',
}

const valueLine: CSSProperties = {
  fontSize: 12,
  color: 'var(--color-muted, #64748b)',
  maxWidth: 200,
  textAlign: 'center',
  wordBreak: 'break-all',
}

export function InstallQrCode({ value, ariaLabel }: InstallQrCodeProps) {
  return (
    <div data-testid="install-qr" style={wrap}>
      <div
        role="img"
        aria-label={ariaLabel ?? value}
        data-testid="install-qr-visual"
        style={square}
      >
        <span style={innerLabel}>{value}</span>
      </div>
      <span data-testid="install-qr-value" style={valueLine}>
        {value}
      </span>
    </div>
  )
}
